import {
    GenericUserViewModel,
    NotificationEventConfigViewModel,
    NotificationEventType,
    NotificationType,
    NotificationTypeConfigViewModel,
} from "@api/web-api-client";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface INotificationsState {
    language: string;
    allNotifications: NotificationTypeConfigViewModel[];
}

const initialState: INotificationsState = {
    language: "",
    allNotifications: [],
};

export const notificationsSlice = createSlice({
    name: "notifications",
    initialState: initialState,
    reducers: {
        setAllNotificationsConfig: (
            state,
            action: PayloadAction<NotificationTypeConfigViewModel[]>,
        ) => {
            const notificationEventTypeValues = Object.values(
                NotificationEventType,
            ).filter((value) => typeof value === "number");

            const notificationTypeValues = Object.values(
                NotificationType,
            ).filter((value) => typeof value === "number");

            const updatedNotifications: NotificationTypeConfigViewModel[] = [];

            for (const eventType of notificationEventTypeValues) {
                const existingEventTypeConfig = action.payload.find(
                    (config) => config.eventType === eventType,
                );

                if (!existingEventTypeConfig) {
                    // If the eventType doesn't exist, add a default one
                    updatedNotifications.push({
                        eventType,
                        notificationEventConfig: notificationTypeValues.map(
                            (type) => ({
                                notificationType: type,
                                notifyCreator: false,
                                notifyReviewers: false,
                                customRecipients: [],
                            }),
                        ),
                    } as any);
                } else {
                    // Validate or add info to existing notificationEventConfig for all notificationTypes
                    const updatedNotificationEventConfig = [
                        ...notificationTypeValues,
                    ].map((type) => {
                        const existingConfig =
                            existingEventTypeConfig.notificationEventConfig.find(
                                (config) => config.notificationType === type,
                            );

                        // Ensure all notificationTypes exist with defaults if missing
                        return (
                            existingConfig || {
                                notificationType: type,
                                notifyCreator: false,
                                notifyReviewers: false,
                                customRecipients: [],
                            }
                        );
                    });

                    updatedNotifications.push({
                        ...existingEventTypeConfig,
                        notificationEventConfig: updatedNotificationEventConfig,
                    } as any);
                }
            }

            state.allNotifications = updatedNotifications;
        },
        updateNotificationCustomRecipientsConfig: (
            state,
            action: PayloadAction<{
                eventType: NotificationEventType;
                notificationType: NotificationType;
                userList: GenericUserViewModel[];
            }>,
        ) => {
            const currEventType = state.allNotifications?.findIndex(
                (x) => x.eventType === action.payload.eventType,
            );

            if (currEventType === -1) {
                return;
            }

            const currNotificationType = state.allNotifications[
                currEventType
            ].notificationEventConfig.findIndex(
                (x) => x.notificationType === action.payload.notificationType,
            );

            if (currNotificationType !== -1) {
                state.allNotifications[currEventType].notificationEventConfig[
                    currNotificationType
                ].customRecipients = action.payload.userList;
            }
        },
        updateNotificationConfig: (
            state,
            action: PayloadAction<{
                eventType: NotificationEventType;
                notificationType: NotificationType;
                value: NotificationEventConfigViewModel;
            }>,
        ) => {
            const eventIndex = state.allNotifications.findIndex(
                (x) => x.eventType === action.payload.eventType,
            );

            if (eventIndex !== -1) {
                // Find the target config within the event
                const notificationConfigIndex = state.allNotifications[
                    eventIndex
                ].notificationEventConfig.findIndex(
                    (x) =>
                        x.notificationType === action.payload.notificationType,
                );

                if (notificationConfigIndex !== -1) {
                    state.allNotifications[eventIndex] = {
                        ...state.allNotifications[eventIndex],
                        notificationEventConfig: state.allNotifications[
                            eventIndex
                        ].notificationEventConfig.map((config, index) =>
                            index === notificationConfigIndex
                                ? action.payload.value
                                : config,
                        ),
                    };
                }
            }
        },
        setNotificationLanguage: (state, action: PayloadAction<string>) => {
            state.language = action.payload;
        },
    },
});

export const {
    setAllNotificationsConfig,
    updateNotificationCustomRecipientsConfig,
    updateNotificationConfig,
    setNotificationLanguage,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
